import React, { useEffect, useState } from 'react';
import { Plus, Search, Users, X, Shield } from 'lucide-react';
import { userAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const ROLE_STYLES = {
  tenant_admin: { color: '#7C3AED', bg: '#F5F3FF' },
  owner: { color: '#2563EB', bg: '#EFF6FF' },
  manager: { color: '#059669', bg: '#ECFDF5' },
  staff: { color: '#D97706', bg: '#FFFBEB' },
  guest: { color: '#6B7280', bg: '#F3F4F6' },
};

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', password: '',
    phone: '', role: 'staff'
  });

  const fetchUsers = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (roleFilter) params.role = roleFilter;
      if (search) params.search = search;
      const res = await userAPI.getAll(params);
      setUsers(res.data.data || []);
      setPagination(res.data.pagination || {});
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, [search, roleFilter]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await userAPI.create(form);
      toast.success('User created successfully');
      setShowForm(false);
      setForm({ firstName: '', lastName: '', email: '', password: '', phone: '', role: 'staff' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create user');
    }
  };

  const handleDeactivate = async (id, name) => {
    if (id === currentUser._id) return toast.error("Can't deactivate yourself");
    if (!window.confirm(`Deactivate ${name}?`)) return;
    try {
      await userAPI.delete(id);
      toast.success('User deactivated');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to deactivate');
    }
  };

  const handleRoleChange = async (id, role) => {
    try {
      await userAPI.update(id, { role });
      toast.success('Role updated');
      fetchUsers();
    } catch {
      toast.error('Role update failed');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Users & Team</h1>
          <p className="page-subtitle">{pagination.total} team members</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          <Plus size={16} /> Invite User
        </button>
      </div>

      <div className="filters-bar">
        <div className="search-bar">
          <Search size={16} />
          <input placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width: 160 }} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
          <option value="">All Roles</option>
          {Object.keys(ROLE_STYLES).map(r => (
            <option key={r} value={r}>{r.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner spinner-lg" /></div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr><th>User</th><th>Email</th><th>Phone</th><th>Role</th><th>Last Login</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr><td colSpan={7}><div className="empty-state"><Users size={40} /><h3>No users found</h3></div></td></tr>
                ) : users.map(u => (
                  <tr key={u._id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div className="avatar avatar-sm">
                          {u.avatar ? <img src={u.avatar} alt="" /> : `${u.firstName?.[0]}${u.lastName?.[0]}`}
                        </div>
                        <div>
                          <div style={{ fontWeight: 500 }}>{u.firstName} {u.lastName}</div>
                          {u._id === currentUser._id && <span style={{ fontSize: 11, color: '#9CA3AF' }}>You</span>}
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: 13 }}>{u.email}</td>
                    <td style={{ fontSize: 13, color: '#6B7280' }}>{u.phone || '—'}</td>
                    <td>
                      <select
                        className="form-select"
                        style={{ padding: '4px 8px', fontSize: 12, width: 130, color: ROLE_STYLES[u.role]?.color, background: ROLE_STYLES[u.role]?.bg }}
                        value={u.role}
                        disabled={u._id === currentUser._id}
                        onChange={e => handleRoleChange(u._id, e.target.value)}
                      >
                        {Object.keys(ROLE_STYLES).filter(r => r !== 'tenant_admin').map(r => (
                          <option key={r} value={r}>{r.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                        ))}
                        {u.role === 'tenant_admin' && <option value="tenant_admin">Admin</option>}
                      </select>
                    </td>
                    <td style={{ fontSize: 13, color: '#6B7280' }}>
                      {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : 'Never'}
                    </td>
                    <td>
                      <span className={`badge ${u.isActive ? 'badge-green' : 'badge-red'}`}>
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {u._id !== currentUser._id && u.isActive && (
                        <button className="btn btn-sm btn-danger" onClick={() => handleDeactivate(u._id, `${u.firstName} ${u.lastName}`)}>
                          Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pagination.pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
            <button key={p} className={`btn btn-sm ${pagination.page === p ? 'btn-primary' : 'btn-secondary'}`} onClick={() => fetchUsers(p)}>{p}</button>
          ))}
        </div>
      )}

      {/* Create User Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Invite Team Member</span>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setShowForm(false)}><X size={16} /></button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="form-grid form-grid-2">
                  <div className="form-group">
                    <label className="form-label">First Name *</label>
                    <input className="form-input" value={form.firstName} onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name *</label>
                    <input className="form-input" value={form.lastName} onChange={e => setForm(p => ({ ...p, lastName: e.target.value }))} required />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input type="email" className="form-input" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Temporary Password *</label>
                  <input type="password" className="form-input" minLength={6} value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} required />
                </div>
                <div className="form-grid form-grid-2">
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input type="tel" className="form-input" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <select className="form-select" value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}>
                      {['owner', 'manager', 'staff', 'guest'].map(r => (
                        <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create User</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
